import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Info,
  Loader2,
  MessageSquare,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { evalsApi, type EvalFinding, type EvalTargetType } from "@/api/evals";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";

/**
 * Auditor panel — request an evaluation and read the verdict.
 *
 * Drops into any detail page (workflow, run, agent) by target. The most
 * recent report is shown expanded; older ones collapse into a short history
 * so a trend is visible without leaving the page.
 *
 * Two presentation rules carry meaning rather than style:
 *  - `limitations` is rendered as prominently as the findings. A report that
 *    looks complete when half the data was missing is the failure mode this
 *    whole feature exists to avoid.
 *  - `inferred` findings are visibly marked. Measured and inferred should
 *    never look alike at a glance.
 */
export function EvalPanel({
  targetType,
  targetId,
  className,
}: {
  targetType: EvalTargetType;
  targetId: string;
  className?: string;
}) {
  const { tenantId, isInstanceAdmin } = useTenant();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  /**
   * Hand a finding to the Auditor chat. The report id goes through the URL
   * (?auditor=…) because the widget lives outside this component's tree —
   * same mechanism the bell already uses to deep-link a support ticket.
   */
  const openAuditor = (reportId: string, findingTitle: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set("auditor", reportId);
    navigate({ search: params.toString() }, { replace: true });
    // Seed the first question so the conversation opens on the finding the
    // user clicked, not on a blank prompt they have to re-explain.
    window.dispatchEvent(
      new CustomEvent("proclaw:auditor-prefill", {
        detail: { message: `About the finding "${findingTitle}" — explain it and what you'd change.` },
      }),
    );
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.evals.byTarget(tenantId ?? "", targetId),
    queryFn: () => evalsApi.list(tenantId!, targetId),
    enabled: !!tenantId && !!targetId,
    // An eval takes tens of seconds; poll while one is in flight so the
    // verdict appears without the user reloading.
    refetchInterval: (q) =>
      q.state.data?.reports.some((r) => r.status === "running") ? 4_000 : false,
  });

  const runEval = useMutation({
    mutationFn: () => evalsApi.run(tenantId!, { targetType, targetId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.evals.byTarget(tenantId ?? "", targetId),
      });
    },
  });

  const reports = data?.reports ?? [];
  const latest = reports[0];
  const running = latest?.status === "running";

  // Fetch the full report for completed AND failed runs — a failure's `error`
  // is the whole point of looking.
  const { data: detail } = useQuery({
    queryKey: queryKeys.evals.detail(tenantId ?? "", latest?.id ?? ""),
    queryFn: () => evalsApi.get(tenantId!, latest!.id),
    enabled:
      !!tenantId && !!latest && (latest.status === "completed" || latest.status === "failed"),
  });

  const verdict = detail?.report.verdict;
  const failureDetail =
    latest?.status === "failed" ? describeFailure(detail?.report.error ?? null) : null;

  return (
    <div className={cn("rounded-lg border bg-card", className)}>
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Evaluation</h3>
          {latest?.headlineSeverity && latest.status === "completed" && (
            <SeverityBadge severity={latest.headlineSeverity} />
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => runEval.mutate()}
          disabled={running || runEval.isPending}
        >
          {running || runEval.isPending ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Evaluating…
            </>
          ) : (
            "Run evaluation"
          )}
        </Button>
      </div>

      <div className="px-4 py-3">
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {!isLoading && reports.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No evaluations yet. Run one to check this{" "}
            {targetType === "agent" ? "agent's setup" : "workflow"} for correctness issues,
            wasted tokens and whether a cheaper model would do.
          </p>
        )}

        {runEval.isError && (
          <p className="text-sm text-destructive">
            Could not start the evaluation. {(runEval.error as Error)?.message}
          </p>
        )}

        {running && (
          <p className="text-sm text-muted-foreground">
            Gathering evidence and scoring — this usually takes under a minute.
          </p>
        )}

        {/* Show the actual reason. "Try again" is useless advice when the
            cause is an exhausted credential — the same click fails forever,
            and the error was sitting on the report row all along. */}
        {latest?.status === "failed" && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-sm font-medium text-destructive">Evaluation failed</p>
            <p className="mt-1 break-words text-xs text-muted-foreground">
              {failureDetail?.error ?? "No detail was recorded."}
            </p>
            {failureDetail?.hint && (
              <p className="mt-1 text-xs text-muted-foreground">{failureDetail.hint}</p>
            )}
          </div>
        )}

        {verdict && (
          <div className="space-y-4">
            <p className="text-sm">{verdict.summary}</p>

            {verdict.findings.length > 0 ? (
              <ul className="space-y-3">
                {verdict.findings.map((f, i) => (
                  <FindingRow
                    key={i}
                    finding={f}
                    onDiscuss={
                      isInstanceAdmin && latest
                        ? () => openAuditor(latest.id, f.title)
                        : undefined
                    }
                  />
                ))}
              </ul>
            ) : (
              <p className="flex items-start gap-2 text-sm text-muted-foreground">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                No issues found.
              </p>
            )}

            {/* Limitations sit at the same level as findings on purpose — they
                qualify everything above them. */}
            {verdict.limitations.length > 0 && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-500">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  What this evaluation could not check
                </p>
                <ul className="list-inside list-disc space-y-0.5 text-xs text-muted-foreground">
                  {verdict.limitations.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              </div>
            )}

            {verdict.checkedAndHealthy.length > 0 && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer select-none">
                  Checked and healthy ({verdict.checkedAndHealthy.length})
                </summary>
                <ul className="mt-1 list-inside list-disc space-y-0.5 pl-1">
                  {verdict.checkedAndHealthy.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </details>
            )}

            {detail?.report.model && (
              <p className="text-[11px] text-muted-foreground">
                Evaluated with {detail.report.model} ·{" "}
                {(detail.report.inputTokens + detail.report.outputTokens).toLocaleString()} tokens
              </p>
            )}
          </div>
        )}

        {reports.length > 1 && (
          <div className="mt-4 border-t pt-3">
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setExpanded((v) => !v)}
            >
              <ChevronDown
                className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
              />
              {reports.length - 1} earlier evaluation{reports.length > 2 ? "s" : ""}
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1.5">
                {reports.slice(1).map((r) => (
                  <li key={r.id} className="flex items-start gap-2 text-xs">
                    <span className="shrink-0 text-muted-foreground">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                    <span className="text-muted-foreground">{r.summary ?? r.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FindingRow({
  finding,
  onDiscuss,
}: {
  finding: EvalFinding;
  onDiscuss?: () => void;
}) {
  const Icon =
    finding.severity === "critical"
      ? AlertTriangle
      : finding.severity === "warning"
        ? AlertTriangle
        : Info;
  const tone =
    finding.severity === "critical"
      ? "text-destructive"
      : finding.severity === "warning"
        ? "text-amber-600 dark:text-amber-500"
        : "text-muted-foreground";

  return (
    <li className="rounded-md border px-3 py-2">
      <div className="flex items-start gap-2">
        <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", tone)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium">{finding.title}</span>
            <Badge variant="outline" className="text-[10px]">
              {finding.category.replace("_", " ")}
            </Badge>
            {/* An inferred finding must never read like a measured one. */}
            {finding.confidence === "inferred" && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                inferred
              </Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{finding.evidence}</p>
          <p className="mt-1 text-xs">{finding.recommendation}</p>
          {finding.estimatedMonthlySavingCents !== null &&
            finding.estimatedMonthlySavingCents > 0 && (
              <p className="mt-1 text-xs font-medium text-emerald-700 dark:text-emerald-500">
                Est. ${(finding.estimatedMonthlySavingCents / 100).toFixed(2)}/month
              </p>
            )}
          {/* Without this the finding is a dead end — it tells you something
              is wrong and gives you nowhere to go. This hands the finding to
              the Auditor with its evidence already in context. */}
          {onDiscuss && (
            <button
              onClick={onDiscuss}
              className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
            >
              <MessageSquare className="h-3 w-3" />
              Discuss or fix this
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Turn a raw provider error into something the reader can act on.
 *
 * The failure that prompted this was a 429 from an exhausted OAuth
 * subscription token — which no amount of re-clicking fixes, and which the
 * old "try running it again" actively misdirected people away from.
 */
function describeFailure(error: string | null): { error: string; hint?: string } | null {
  if (!error) return null;
  const lower = error.toLowerCase();

  if (lower.includes("429") || lower.includes("rate_limit")) {
    return {
      error: error.slice(0, 300),
      hint: "The model credential is rate limited or its subscription is exhausted. A tenant admin can point evals at a different model under Settings → Model Policy.",
    };
  }
  if (lower.includes("401") || lower.includes("403") || lower.includes("authentication")) {
    return {
      error: error.slice(0, 300),
      hint: "The model credential was rejected. It may have expired and need re-authorising.",
    };
  }
  if (lower.includes("no anthropic credential")) {
    return {
      error: error.slice(0, 300),
      hint: "No usable credential is configured for evaluations.",
    };
  }
  return { error: error.slice(0, 300) };
}

function SeverityBadge({ severity }: { severity: string }) {
  if (severity === "none") {
    return (
      <Badge variant="outline" className="text-[10px] text-emerald-700 dark:text-emerald-500">
        healthy
      </Badge>
    );
  }
  return (
    <Badge
      variant={severity === "critical" ? "danger" : severity === "warning" ? "warning" : "outline"}
      className="text-[10px]"
    >
      {severity}
    </Badge>
  );
}
